<template>
  <div class="ohlc-explorer-tab p-4">
    <div class="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4 mb-4 sm:items-end min-w-0">
      <!-- Symbol Selector -->
      <div class="form-control w-full sm:max-w-xs">
        <label class="label">
          <span class="label-text">Symbol</span>
        </label>
        <div class="relative w-full">
          <input
            v-model="symbolInput"
            type="text"
            placeholder="Type or select symbol"
            class="input input-bordered w-full"
            @focus="showDropdown = true"
            @input="filterSymbols"
            @keydown.enter="selectSymbol(symbolInput)"
            @click.stop
          >
          <ul
            v-if="showDropdown && filteredSymbols.length > 0"
            class="absolute left-0 top-full mt-1 z-50 menu p-2 shadow-lg bg-base-100 rounded-box w-full max-h-60 overflow-y-auto border border-base-300"
            @click.stop
          >
            <li
              v-for="sym in filteredSymbols"
              :key="sym"
            >
              <a @click.stop="selectSymbol(sym)">{{ sym }}</a>
            </li>
          </ul>
        </div>
      </div>

      <!-- Start Date -->
      <div class="form-control w-full sm:w-auto">
        <label class="label">
          <span class="label-text">Start Date</span>
        </label>
        <input
          v-model="startDate"
          type="date"
          class="input input-bordered"
        >
      </div>

      <!-- End Date -->
      <div class="form-control w-full sm:w-auto">
        <label class="label">
          <span class="label-text">End Date</span>
        </label>
        <input
          v-model="endDate"
          type="date"
          class="input input-bordered"
        >
      </div>

      <!-- Timeframe Selector -->
      <div class="form-control w-full sm:w-auto">
        <label class="label">
          <span class="label-text">Timeframe</span>
        </label>
        <div class="join flex-wrap">
          <button
            v-for="tf in timeframes"
            :key="tf.value"
            class="join-item btn btn-xs sm:btn-sm min-w-0 px-2"
            :class="{ 'btn-active': selectedTimeframe === tf.value }"
            @click="selectedTimeframe = tf.value"
          >
            {{ tf.label }}
          </button>
        </div>
      </div>

      <!-- Fetch Button -->
      <div class="form-control w-full sm:w-auto">
        <label class="label">
          <span class="label-text">&nbsp;</span>
        </label>
        <button
          class="btn btn-primary w-full sm:w-auto"
          :disabled="isLoading || !symbol"
          @click="fetchOHLCData"
        >
          <span
            v-if="isLoading"
            class="loading loading-spinner loading-sm"
          />
          {{ isLoading ? 'Loading...' : 'Load Data' }}
        </button>
      </div>
    </div>

    <!-- Overlays Section -->
    <div class="mb-4 p-3 bg-base-200 rounded-lg">
      <div class="flex items-center justify-between mb-2">
        <span class="font-semibold text-sm">Overlays (Close Price Lines)</span>
        <div class="relative">
          <button
            class="btn btn-sm btn-ghost"
            @click.stop="showOverlayDropdown = !showOverlayDropdown"
          >
            + Add
          </button>
          <div
            v-if="showOverlayDropdown"
            class="absolute right-0 top-full mt-1 z-50 p-2 shadow-lg bg-base-100 rounded-box w-64 border border-base-300"
            @click.stop
          >
            <input
              ref="overlayInput"
              v-model="overlaySymbolInput"
              type="text"
              placeholder="Type symbol (e.g., FARTCOIN/USDT)"
              class="input input-bordered input-sm w-full mb-2"
              @input="filterOverlaySymbols"
              @keydown.enter="addOverlay(overlaySymbolInput)"
            >
            <ul class="menu menu-sm max-h-40 overflow-y-auto">
              <li
                v-for="sym in filteredOverlaySymbols"
                :key="sym"
              >
                <a @click.stop="addOverlay(sym)">{{ sym }}</a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Overlay List -->
      <div
        v-if="overlays.length > 0"
        class="flex flex-wrap gap-2"
      >
        <div
          v-for="overlay in overlays"
          :key="overlay.id"
          class="flex items-center gap-2 bg-base-300 rounded-full px-3 py-1"
        >
          <span
            class="w-3 h-3 rounded-full"
            :style="{ backgroundColor: overlay.color }"
          />
          <span class="text-sm">{{ overlay.symbol }}</span>
          <button
            class="btn btn-ghost btn-xs btn-circle"
            @click="removeOverlay(overlay.id)"
          >
            ×
          </button>
        </div>
      </div>
      <div
        v-else
        class="text-sm text-base-content/50"
      >
        No overlays added. Click "+ Add" to compare with other symbols.
      </div>
    </div>

    <!-- Error Message -->
    <div
      v-if="error"
      class="alert alert-error mb-4"
    >
      <span>{{ error }}</span>
    </div>

    <!-- Chart Container -->
    <div
      ref="chartContainer"
      class="chart-container bg-base-200 rounded-lg"
      :class="{ 'flex items-center justify-center': !hasData }"
    >
      <div
        v-if="!hasData && !isLoading"
        class="text-center text-base-content/50"
      >
        <p class="text-lg">
          Select a symbol and date range to view OHLC data
        </p>
      </div>
    </div>

    <!-- Data Summary -->
    <div
      v-if="hasData"
      class="mt-4 stats shadow w-full"
    >
      <div class="stat">
        <div class="stat-title">
          Candles
        </div>
        <div class="stat-value text-lg">
          {{ chartData.length }}
        </div>
      </div>
      <div class="stat">
        <div class="stat-title">
          Period High
        </div>
        <div class="stat-value text-lg text-success">
          ${{ formatPrice(periodHigh) }}
        </div>
      </div>
      <div class="stat">
        <div class="stat-title">
          Period Low
        </div>
        <div class="stat-value text-lg text-error">
          ${{ formatPrice(periodLow) }}
        </div>
      </div>
      <div class="stat">
        <div class="stat-title">
          Change
        </div>
        <div
          class="stat-value text-lg"
          :class="priceChange >= 0 ? 'text-success' : 'text-error'"
        >
          {{ priceChange >= 0 ? '+' : '' }}{{ priceChange.toFixed(2) }}%
        </div>
      </div>
    </div>

    <!-- Directional Short Analytics Section -->
    <div
      v-if="hasData && hasReferenceAsset"
      class="mt-6"
    >
      <div class="divider">
        <span class="text-lg font-semibold">Directional Short Analytics</span>
      </div>

      <!-- Analytics Configuration -->
      <div class="mb-4 p-4 bg-base-200 rounded-lg">
        <div class="flex flex-wrap gap-4 items-end">
          <!-- Reference Asset (BTC) -->
          <div class="form-control w-full sm:w-auto">
            <label class="label">
              <span class="label-text">Reference Asset</span>
            </label>
            <select
              v-model="referenceAsset"
              class="select select-bordered select-sm"
              @change="computeAnalytics"
            >
              <option value="BTC/USDT">
                BTC/USDT
              </option>
              <option value="ETH/USDT">
                ETH/USDT
              </option>
            </select>
          </div>

          <!-- Rolling Window -->
          <div class="form-control w-full sm:w-auto">
            <label class="label">
              <span class="label-text">Rolling Window</span>
            </label>
            <select
              v-model.number="rollingWindow"
              class="select select-bordered select-sm"
              @change="computeAnalytics"
            >
              <option :value="12">
                12 bars
              </option>
              <option :value="24">
                24 bars
              </option>
              <option :value="48">
                48 bars
              </option>
              <option :value="96">
                96 bars
              </option>
              <option :value="288">
                288 bars
              </option>
            </select>
          </div>

          <!-- Lag Range -->
          <div class="form-control w-full sm:w-auto">
            <label class="label">
              <span class="label-text">Lag Range</span>
            </label>
            <select
              v-model.number="lagRange"
              class="select select-bordered select-sm"
              @change="computeAnalytics"
            >
              <option :value="5">
                +/- 5 bars
              </option>
              <option :value="10">
                +/- 10 bars
              </option>
              <option :value="20">
                +/- 20 bars
              </option>
            </select>
          </div>

          <!-- Correlation Threshold -->
          <div class="form-control w-full sm:w-auto">
            <label class="label">
              <span class="label-text">Corr Threshold</span>
            </label>
            <input
              v-model.number="correlationThreshold"
              type="number"
              step="0.05"
              min="0"
              max="1"
              class="input input-bordered input-sm w-20"
              @change="computeAnalytics"
            >
          </div>

          <!-- Volatility Threshold (sigma) -->
          <div class="form-control w-full sm:w-auto">
            <label class="label">
              <span class="label-text">Vol Threshold (σ)</span>
            </label>
            <input
              v-model.number="volatilityThreshold"
              type="number"
              step="0.5"
              min="1"
              max="5"
              class="input input-bordered input-sm w-20"
              @change="computeAnalytics"
            >
          </div>
        </div>
      </div>

      <!-- Insufficient Data Warning -->
      <div
        v-if="!analyticsDataStatus.ok && analyticsDataStatus.message"
        class="mb-4 alert alert-warning"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="stroke-current shrink-0 h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
        <span>{{ analyticsDataStatus.message }}</span>
      </div>

      <!-- Short Eligibility Status -->
      <div
        v-if="analyticsDataStatus.ok"
        class="mb-4"
      >
        <div class="flex flex-wrap gap-4 items-center mb-3">
          <div
            class="badge badge-lg gap-2"
            :class="shortEligibility.eligible ? 'badge-success' : 'badge-error'"
          >
            <span
              class="w-2 h-2 rounded-full"
              :class="shortEligibility.eligible ? 'bg-green-300 animate-pulse' : 'bg-red-300'"
            />
            Short {{ shortEligibility.eligible ? 'ELIGIBLE' : 'NOT ELIGIBLE' }}
          </div>
          <div class="text-sm text-base-content/70">
            <span
              v-for="(reason, idx) in shortEligibility.reasons"
              :key="idx"
              class="mr-3"
            >
              <span :class="reason.pass ? 'text-success' : 'text-error'">{{ reason.pass ? '✓' : '✗' }}</span>
              {{ reason.shortDesc }}
            </span>
          </div>
        </div>

        <!-- Detailed Explanations -->
        <div class="space-y-2">
          <div
            v-for="(reason, idx) in shortEligibility.reasons"
            :key="idx"
            class="p-3 rounded-lg text-sm"
            :class="reason.pass ? 'bg-success/10 border border-success/30' : 'bg-error/10 border border-error/30'"
          >
            <div class="flex items-start gap-2">
              <span
                class="font-bold mt-0.5"
                :class="reason.pass ? 'text-success' : 'text-error'"
              >
                {{ reason.pass ? '✓' : '✗' }}
              </span>
              <div>
                <span class="font-semibold">{{ reason.label }}:</span>
                <span class="text-base-content/80 ml-1">{{ reason.detail }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Current Analytics Stats -->
      <div
        v-if="analyticsDataStatus.ok"
        class="stats stats-vertical sm:stats-horizontal shadow w-full mb-4"
      >
        <div class="stat">
          <div class="stat-title">
            Current Beta
          </div>
          <div class="stat-value text-lg">
            {{ analyticsData.currentBeta?.toFixed(3) || 'N/A' }}
          </div>
          <div class="stat-desc">
            Downside: {{ analyticsData.currentDownsideBeta?.toFixed(3) || 'N/A' }}
          </div>
        </div>
        <div class="stat">
          <div class="stat-title">
            Correlation
          </div>
          <div class="stat-value text-lg">
            {{ analyticsData.currentCorrelation?.toFixed(3) || 'N/A' }}
          </div>
          <div class="stat-desc">
            Spearman: {{ analyticsData.currentSpearman?.toFixed(3) || 'N/A' }}
          </div>
        </div>
        <div class="stat">
          <div class="stat-title">
            Lead-Lag
          </div>
          <div class="stat-value text-lg">
            k* = {{ analyticsData.dominantLag || 'N/A' }}
          </div>
          <div class="stat-desc">
            ρ* = {{ analyticsData.lagCorrelation?.toFixed(3) || 'N/A' }}
          </div>
        </div>
        <div class="stat">
          <div class="stat-title">
            BTC Regime
          </div>
          <div
            class="stat-value text-lg"
            :class="regimeColor"
          >
            {{ analyticsData.btcRegime || 'N/A' }}
          </div>
          <div class="stat-desc">
            Vol: {{ analyticsData.btcVolatility?.toFixed(4) || 'N/A' }}
          </div>
        </div>
      </div>

      <!-- Analytics Charts Container -->
      <div
        v-if="analyticsDataStatus.ok"
        class="grid grid-cols-1 xl:grid-cols-2 gap-4"
      >
        <!-- Beta Chart -->
        <div class="bg-base-200 rounded-lg p-2">
          <h4 class="text-sm font-semibold mb-2 px-2">
            Rolling Beta (β)
          </h4>
          <div
            ref="betaChartContainer"
            class="analytics-chart"
          />
        </div>

        <!-- Correlation Chart -->
        <div class="bg-base-200 rounded-lg p-2">
          <h4 class="text-sm font-semibold mb-2 px-2">
            Rolling Correlation
          </h4>
          <div
            ref="correlationChartContainer"
            class="analytics-chart"
          />
        </div>

        <!-- Lead-Lag CCF Chart -->
        <div class="bg-base-200 rounded-lg p-2">
          <h4 class="text-sm font-semibold mb-2 px-2">
            Cross-Correlation Function (CCF)
          </h4>
          <div
            ref="ccfChartContainer"
            class="analytics-chart"
          />
        </div>

        <!-- Regime & Volatility Chart -->
        <div class="bg-base-200 rounded-lg p-2">
          <h4 class="text-sm font-semibold mb-2 px-2">
            BTC Volatility & Regime
          </h4>
          <div
            ref="regimeChartContainer"
            class="analytics-chart"
          />
        </div>
      </div>
    </div>

    <!-- No Reference Asset Message -->
    <div
      v-else-if="hasData && !hasReferenceAsset"
      class="mt-6 alert alert-info"
    >
      <span>Add BTC/USDT or ETH/USDT as an overlay to enable Directional Short Analytics</span>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import Plotly from 'plotly.js-dist-min';
import api from '@/api/axios';

defineProps({
  backtest: {
    type: Object,
    default: null,
  },
});

const commonSymbols = [
  'BTC/USDT',
  'ETH/USDT',
  'SOL/USDT',
  'XRP/USDT',
  'BNB/USDT',
  'ADA/USDT',
  'DOGE/USDT',
  'AVAX/USDT',
  'DOT/USDT',
  'MATIC/USDT',
  'LINK/USDT',
  'UNI/USDT',
  'ATOM/USDT',
  'LTC/USDT',
  'FIL/USDT',
  'FARTCOIN/USDT',
  'PENGU/USDT',
  'WIF/USDT',
  'TRUMP/USDT',
  'PEPE/USDT',
];

const overlayColors = [
  '#ff6b6b', // red
  '#4ecdc4', // teal
  '#ffe66d', // yellow
  '#95e1d3', // mint
  '#f38181', // coral
  '#aa96da', // purple
  '#fcbad3', // pink
  '#a8d8ea', // light blue
  '#ffb347', // orange
  '#98d8c8', // seafoam
];

const timeframes = [
  { label: '1m', value: '1m' },
  { label: '5m', value: '5m' },
  { label: '15m', value: '15m' },
  { label: '1h', value: '1h' },
  { label: '4h', value: '4h' },
  { label: '1d', value: '1d' },
];

const chartContainer = ref(null);
const symbolInput = ref('BTC/USDT');
const symbol = ref('BTC/USDT');
const showDropdown = ref(false);
const filteredSymbols = ref([...commonSymbols]);
const selectedTimeframe = ref('1d');
const startDate = ref('');
const endDate = ref('');
const chartData = ref([]);
const isLoading = ref(false);
const error = ref(null);

// Overlay state
const overlays = ref([]);
const nextOverlayId = ref(1);
const showOverlayDropdown = ref(false);
const overlaySymbolInput = ref('');
const filteredOverlaySymbols = ref([...commonSymbols]);
const overlayInput = ref(null);

// Analytics chart containers
const betaChartContainer = ref(null);
const correlationChartContainer = ref(null);
const ccfChartContainer = ref(null);
const regimeChartContainer = ref(null);

// Analytics configuration
const referenceAsset = ref('BTC/USDT');
const rollingWindow = ref(48);
const lagRange = ref(10);
const correlationThreshold = ref(0.5);
const volatilityThreshold = ref(2);

// Analytics data
const analyticsData = ref({
  dates: [],
  rollingBeta: [],
  rollingDownsideBeta: [],
  rollingCorrelation: [],
  rollingSpearman: [],
  ccfLags: [],
  ccfValues: [],
  rollingVolatility: [],
  regimeLabels: [],
  currentBeta: null,
  currentDownsideBeta: null,
  currentCorrelation: null,
  currentSpearman: null,
  dominantLag: null,
  lagCorrelation: null,
  btcRegime: null,
  btcVolatility: null,
  betaStable: false,
});

const hasData = computed(() => chartData.value.length > 0);

const hasReferenceAsset = computed(() => {
  // Check if BTC or ETH is either the main symbol or an overlay
  const refAssets = ['BTC/USDT', 'ETH/USDT'];
  const mainIsRef = refAssets.includes(symbol.value);
  const overlayHasRef = overlays.value.some(o => refAssets.includes(o.symbol));
  // We need both a target asset and a reference asset
  return (mainIsRef && overlays.value.length > 0) || (!mainIsRef && overlayHasRef);
});

const shortEligibility = computed(() => {
  const reasons = [];
  const data = analyticsData.value;

  // Check correlation threshold
  const corrPass = data.currentCorrelation !== null && data.currentCorrelation >= correlationThreshold.value;
  const corrValue = data.currentCorrelation?.toFixed(3) || 'N/A';
  reasons.push({
    label: 'Correlation',
    pass: corrPass,
    shortDesc: `Corr ≥ ${correlationThreshold.value}`,
    detail: corrPass
      ? `Correlation is ${corrValue}, above the ${correlationThreshold.value} threshold. The target asset is sufficiently correlated with BTC, making BTC a reliable timing signal.`
      : `Correlation is ${corrValue}, below the ${correlationThreshold.value} threshold. The target asset is moving too independently from BTC right now. BTC signals may not be reliable for timing entries. Consider waiting for correlation to increase or lowering the threshold.`,
  });

  // Check beta stability
  const betaPass = data.betaStable;
  const betaValue = data.currentBeta?.toFixed(3) || 'N/A';
  reasons.push({
    label: 'Beta Stability',
    pass: betaPass,
    shortDesc: 'Beta stable',
    detail: betaPass
      ? `Beta is stable at ${betaValue}. The relationship between BTC and the target is consistent, making beta-based predictions reliable.`
      : `Beta is unstable (currently ${betaValue}). The sensitivity of the target to BTC is changing rapidly, which makes the relationship unreliable. This often happens during regime transitions or when the asset is being driven by idiosyncratic factors (news, listings, etc.).`,
  });

  // Check lead-lag (BTC leads target)
  const lagPass = data.dominantLag !== null && data.dominantLag > 0;
  const lagValue = data.dominantLag;
  const lagCorr = data.lagCorrelation?.toFixed(3) || 'N/A';
  reasons.push({
    label: 'Lead-Lag',
    pass: lagPass,
    shortDesc: 'BTC leads (k* > 0)',
    detail: lagPass
      ? `BTC leads the target by ${lagValue} bar(s) with correlation ${lagCorr}. This means BTC moves first, and the target follows ${lagValue} bar(s) later — giving you a timing edge to enter shorts after BTC drops.`
      : lagValue === 0
        ? `BTC and target are moving synchronously (k*=0). There's no lead time to exploit — both assets move together. You won't have an early warning from BTC.`
        : lagValue < 0
          ? `The target is actually leading BTC by ${Math.abs(lagValue)} bar(s) (k*=${lagValue}). This is the opposite of what we need — BTC can't be used as an early warning signal here.`
          : `Unable to determine lead-lag relationship. Need more data or the relationship may be too noisy.`,
  });

  const eligible = corrPass && betaPass && lagPass;
  return { eligible, reasons };
});

const regimeColor = computed(() => {
  const regime = analyticsData.value.btcRegime;
  if (regime === 'Crash') return 'text-error';
  if (regime === 'High Vol') return 'text-warning';
  if (regime === 'Low Vol') return 'text-success';
  return 'text-base-content';
});

// Check for insufficient data
const analyticsDataStatus = computed(() => {
  if (!hasData.value || !hasReferenceAsset.value) {
    return { ok: false, message: null };
  }

  const refAssets = ['BTC/USDT', 'ETH/USDT'];
  let targetData, refData;

  if (refAssets.includes(symbol.value)) {
    refData = chartData.value;
    targetData = overlays.value[0]?.data;
  } else {
    targetData = chartData.value;
    const refOverlay = overlays.value.find(o => o.symbol === referenceAsset.value);
    refData = refOverlay?.data;
  }

  if (!targetData || !refData) {
    return { ok: false, message: 'Missing target or reference data.' };
  }

  // Count aligned data points
  const targetMap = new Map(targetData.map(d => [new Date(d.timestamp).getTime(), d]));
  let alignedCount = 0;
  for (const rd of refData) {
    const ts = new Date(rd.timestamp).getTime();
    if (targetMap.has(ts)) alignedCount++;
  }

  const requiredBars = rollingWindow.value + 10;

  if (alignedCount < requiredBars) {
    return {
      ok: false,
      message: `Insufficient data: ${alignedCount} aligned bars available, but ${requiredBars} required (rolling window ${rollingWindow.value} + 10). Try reducing the rolling window to ${Math.max(12, alignedCount - 15)} bars, extending the date range, or using a shorter timeframe.`,
      alignedCount,
      requiredBars,
    };
  }

  return { ok: true, message: null, alignedCount };
});

const periodHigh = computed(() => {
  if (!hasData.value) return 0;
  return Math.max(...chartData.value.map(c => c.high));
});

const periodLow = computed(() => {
  if (!hasData.value) return 0;
  return Math.min(...chartData.value.map(c => c.low));
});

const priceChange = computed(() => {
  if (!hasData.value || chartData.value.length < 2) return 0;
  const first = chartData.value[0].open;
  const last = chartData.value[chartData.value.length - 1].close;
  return ((last - first) / first) * 100;
});

const formatPrice = (price) => {
  if (typeof price !== 'number') return '0.00';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price);
};

const filterSymbols = () => {
  const query = symbolInput.value.toUpperCase();
  filteredSymbols.value = commonSymbols.filter(s => s.includes(query));
  showDropdown.value = true;
};

const selectSymbol = (sym) => {
  symbolInput.value = sym;
  symbol.value = sym;
  showDropdown.value = false;
};

const filterOverlaySymbols = () => {
  const query = overlaySymbolInput.value.toUpperCase();
  filteredOverlaySymbols.value = commonSymbols.filter(s =>
    s.includes(query) && s !== symbol.value && !overlays.value.some(o => o.symbol === s)
  );
};

const getNextColor = () => {
  const usedColors = overlays.value.map(o => o.color);
  return overlayColors.find(c => !usedColors.includes(c)) || overlayColors[overlays.value.length % overlayColors.length];
};

const addOverlay = async (sym) => {
  const normalizedSymbol = sym.toUpperCase().trim();
  if (!normalizedSymbol || normalizedSymbol === symbol.value) {
    error.value = 'Cannot add the same symbol as the main chart';
    return;
  }
  if (overlays.value.some(o => o.symbol === normalizedSymbol)) {
    error.value = `${normalizedSymbol} is already added`;
    return;
  }

  // Close dropdown and clear input
  showOverlayDropdown.value = false;
  overlaySymbolInput.value = '';
  filterOverlaySymbols();

  try {
    const response = await api.get('/api/market/ohlcv/', {
      params: {
        symbol: normalizedSymbol,
        timeframe: selectedTimeframe.value,
        start_date: startDate.value,
        end_date: endDate.value,
      },
    });

    if (response.data && Array.isArray(response.data) && response.data.length > 0) {
      overlays.value.push({
        id: nextOverlayId.value++,
        symbol: normalizedSymbol,
        data: response.data,
        color: getNextColor(),
      });
      renderChart();
    } else {
      error.value = `No data found for ${normalizedSymbol}`;
    }
  } catch (err) {
    console.error('Error fetching overlay data:', err);
    error.value = `Failed to load ${normalizedSymbol}: ${err.response?.data?.message || err.message}`;
  }
};

const removeOverlay = (id) => {
  overlays.value = overlays.value.filter(o => o.id !== id);
  renderChart();
};

const refreshOverlays = async () => {
  if (overlays.value.length === 0) return;

  const refreshPromises = overlays.value.map(async (overlay) => {
    try {
      const response = await api.get('/api/market/ohlcv/', {
        params: {
          symbol: overlay.symbol,
          timeframe: selectedTimeframe.value,
          start_date: startDate.value,
          end_date: endDate.value,
        },
      });
      if (response.data && Array.isArray(response.data)) {
        overlay.data = response.data;
      }
    } catch (err) {
      console.error(`Error refreshing ${overlay.symbol}:`, err);
    }
  });

  await Promise.all(refreshPromises);
};

const fetchOHLCData = async () => {
  // Update symbol from input (in case user typed without pressing Enter)
  if (symbolInput.value && symbolInput.value !== symbol.value) {
    symbol.value = symbolInput.value.toUpperCase().trim();
  }

  if (!symbol.value) return;

  isLoading.value = true;
  error.value = null;

  try {
    const response = await api.get('/api/market/ohlcv/', {
      params: {
        symbol: symbol.value,
        timeframe: selectedTimeframe.value,
        start_date: startDate.value,
        end_date: endDate.value,
      },
    });

    if (response.data && Array.isArray(response.data)) {
      chartData.value = response.data;
      await refreshOverlays();
      renderChart();
    } else {
      error.value = 'No data returned from API';
    }
  } catch (err) {
    console.error('Error fetching OHLC data:', err);
    error.value = err.response?.data?.message || err.message || 'Failed to fetch data';
  } finally {
    isLoading.value = false;
  }
};

const renderChart = () => {
  if (!chartContainer.value || !hasData.value) return;

  const dates = chartData.value.map(c => new Date(c.timestamp));
  const opens = chartData.value.map(c => c.open);
  const highs = chartData.value.map(c => c.high);
  const lows = chartData.value.map(c => c.low);
  const closes = chartData.value.map(c => c.close);

  // Main candlestick trace
  const traces = [
    {
      x: dates,
      open: opens,
      high: highs,
      low: lows,
      close: closes,
      type: 'candlestick',
      increasing: { line: { color: '#26a69a' }, fillcolor: '#26a69a' },
      decreasing: { line: { color: '#ef5350' }, fillcolor: '#ef5350' },
      name: symbol.value,
      yaxis: 'y',
    },
  ];

  // Build layout with dynamic Y-axes
  const layout = {
    title: {
      text: `${symbol.value} - ${selectedTimeframe.value}`,
      font: { size: 16 },
    },
    xaxis: {
      title: 'Date',
      rangeslider: { visible: false },
      type: 'date',
      domain: [0, overlays.value.length > 0 ? 0.85 : 1], // Make room for right axes
    },
    yaxis: {
      title: symbol.value,
      side: 'left',
      autorange: true,
      fixedrange: false,
      showgrid: true,
      gridcolor: 'rgba(107, 114, 128, 0.2)',
    },
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
    font: {
      color: '#9ca3af',
    },
    margin: { t: 50, b: 50, l: 70, r: overlays.value.length > 0 ? 70 + (overlays.value.length * 50) : 30 },
    showlegend: overlays.value.length > 0,
    legend: {
      x: 0,
      y: 1.1,
      orientation: 'h',
    },
  };

  // Add overlay traces and Y-axes
  overlays.value.forEach((overlay, index) => {
    const axisNum = index + 2; // y2, y3, y4, etc.
    const yaxisKey = `yaxis${axisNum}`;
    const yaxisRef = `y${axisNum}`;

    // Add trace for overlay
    const overlayDates = overlay.data.map(c => new Date(c.timestamp));
    const overlayCloses = overlay.data.map(c => c.close);

    traces.push({
      x: overlayDates,
      y: overlayCloses,
      type: 'scatter',
      mode: 'lines',
      name: overlay.symbol,
      yaxis: yaxisRef,
      line: { color: overlay.color, width: 2 },
    });

    // Add Y-axis for this overlay
    layout[yaxisKey] = {
      title: {
        text: overlay.symbol,
        font: { color: overlay.color },
      },
      titlefont: { color: overlay.color },
      tickfont: { color: overlay.color },
      overlaying: 'y',
      side: 'right',
      position: 1 - (index * 0.08), // Stack axes on right side
      autorange: true,
      showgrid: false,
      anchor: 'free',
    };
  });

  const config = {
    responsive: true,
    displayModeBar: false,
  };

  Plotly.react(chartContainer.value, traces, layout, config);

  // Compute analytics after main chart renders
  if (hasReferenceAsset.value) {
    computeAnalytics();
  }
};

// ============================================
// Statistical Calculation Functions
// ============================================

// Calculate log returns from price array
const calculateLogReturns = (prices) => {
  const returns = [];
  for (let i = 1; i < prices.length; i++) {
    if (prices[i] > 0 && prices[i - 1] > 0) {
      returns.push(Math.log(prices[i] / prices[i - 1]));
    } else {
      returns.push(0);
    }
  }
  return returns;
};

// Calculate mean of array
const mean = (arr) => {
  if (arr.length === 0) return 0;
  return arr.reduce((a, b) => a + b, 0) / arr.length;
};

// Calculate standard deviation
const std = (arr) => {
  if (arr.length < 2) return 0;
  const m = mean(arr);
  const variance = arr.reduce((sum, val) => sum + Math.pow(val - m, 2), 0) / (arr.length - 1);
  return Math.sqrt(variance);
};

// Calculate Pearson correlation
const pearsonCorrelation = (x, y) => {
  if (x.length !== y.length || x.length < 2) return 0;
  const n = x.length;
  const mx = mean(x);
  const my = mean(y);
  const sx = std(x);
  const sy = std(y);
  if (sx === 0 || sy === 0) return 0;

  let sum = 0;
  for (let i = 0; i < n; i++) {
    sum += (x[i] - mx) * (y[i] - my);
  }
  return sum / ((n - 1) * sx * sy);
};

// Calculate Spearman rank correlation
const spearmanCorrelation = (x, y) => {
  if (x.length !== y.length || x.length < 2) return 0;
  const n = x.length;

  // Create ranked arrays
  const rankArray = (arr) => {
    const sorted = [...arr].map((v, i) => ({ v, i })).sort((a, b) => a.v - b.v);
    const ranks = new Array(n);
    for (let i = 0; i < n; i++) {
      ranks[sorted[i].i] = i + 1;
    }
    return ranks;
  };

  const rankX = rankArray(x);
  const rankY = rankArray(y);

  // Calculate Pearson correlation of ranks
  return pearsonCorrelation(rankX, rankY);
};

// Calculate rolling OLS beta (y = alpha + beta * x)
const calculateBeta = (x, y) => {
  if (x.length !== y.length || x.length < 2) return { beta: 0, alpha: 0 };
  const n = x.length;
  const mx = mean(x);
  const my = mean(y);

  let numerator = 0;
  let denominator = 0;
  for (let i = 0; i < n; i++) {
    numerator += (x[i] - mx) * (y[i] - my);
    denominator += Math.pow(x[i] - mx, 2);
  }

  if (denominator === 0) return { beta: 0, alpha: my };
  const beta = numerator / denominator;
  const alpha = my - beta * mx;
  return { beta, alpha };
};

// Calculate cross-correlation function for lags [-L, +L]
const calculateCCF = (x, y, maxLag) => {
  const lags = [];
  const correlations = [];

  for (let lag = -maxLag; lag <= maxLag; lag++) {
    let xSlice, ySlice;
    if (lag >= 0) {
      // y leads x (or synchronous)
      xSlice = x.slice(lag);
      ySlice = y.slice(0, y.length - lag);
    } else {
      // x leads y
      xSlice = x.slice(0, x.length + lag);
      ySlice = y.slice(-lag);
    }

    if (xSlice.length >= 5) {
      lags.push(lag);
      correlations.push(pearsonCorrelation(xSlice, ySlice));
    }
  }

  return { lags, correlations };
};

// Calculate rolling statistics
const calculateRollingStats = (targetReturns, refReturns, dates, window) => {
  const n = targetReturns.length;
  const rollingBeta = [];
  const rollingDownsideBeta = [];
  const rollingCorr = [];
  const rollingSpearman = [];
  const rollingVol = [];
  const validDates = [];

  for (let i = window; i < n; i++) {
    const targetWindow = targetReturns.slice(i - window, i);
    const refWindow = refReturns.slice(i - window, i);

    // Rolling beta
    const { beta } = calculateBeta(refWindow, targetWindow);
    rollingBeta.push(beta);

    // Downside beta (only when ref returns < 0)
    const downsideIndices = [];
    for (let j = 0; j < refWindow.length; j++) {
      if (refWindow[j] < 0) downsideIndices.push(j);
    }
    if (downsideIndices.length >= 3) {
      const downsideRef = downsideIndices.map(j => refWindow[j]);
      const downsideTarget = downsideIndices.map(j => targetWindow[j]);
      const { beta: dsb } = calculateBeta(downsideRef, downsideTarget);
      rollingDownsideBeta.push(dsb);
    } else {
      rollingDownsideBeta.push(null);
    }

    // Rolling correlation
    rollingCorr.push(pearsonCorrelation(refWindow, targetWindow));
    rollingSpearman.push(spearmanCorrelation(refWindow, targetWindow));

    // Rolling volatility of reference
    rollingVol.push(std(refWindow));

    validDates.push(dates[i]);
  }

  return { rollingBeta, rollingDownsideBeta, rollingCorr, rollingSpearman, rollingVol, validDates };
};

// Classify BTC regime based on volatility
const classifyRegime = (volatility, volHistory) => {
  if (volHistory.length < 10) return 'Unknown';

  const recentVol = volHistory.slice(-20);
  const avgVol = mean(recentVol.filter(v => v !== null));
  const stdVol = std(recentVol.filter(v => v !== null));

  if (volatility > avgVol + 2 * stdVol) return 'Crash';
  if (volatility > avgVol + stdVol) return 'High Vol';
  return 'Low Vol';
};

// Main analytics computation function
const computeAnalytics = () => {
  if (!hasData.value || !hasReferenceAsset.value) return;

  // Determine target and reference data
  let targetData, refData;
  const refAssets = ['BTC/USDT', 'ETH/USDT'];

  if (refAssets.includes(symbol.value)) {
    // Main symbol is the reference, first overlay is target
    refData = chartData.value;
    targetData = overlays.value[0]?.data;
  } else {
    // Main symbol is target, find reference in overlays
    targetData = chartData.value;
    const refOverlay = overlays.value.find(o => o.symbol === referenceAsset.value);
    refData = refOverlay?.data;
  }

  if (!targetData || !refData || targetData.length < rollingWindow.value + 10) {
    console.warn('Insufficient data for analytics');
    return;
  }

  // Align data by timestamp
  const targetMap = new Map(targetData.map(d => [new Date(d.timestamp).getTime(), d]));
  const alignedTarget = [];
  const alignedRef = [];
  const alignedDates = [];

  for (const rd of refData) {
    const ts = new Date(rd.timestamp).getTime();
    const td = targetMap.get(ts);
    if (td) {
      alignedTarget.push(td.close);
      alignedRef.push(rd.close);
      alignedDates.push(new Date(rd.timestamp));
    }
  }

  if (alignedTarget.length < rollingWindow.value + 10) {
    console.warn('Insufficient aligned data');
    return;
  }

  // Calculate log returns
  const targetReturns = calculateLogReturns(alignedTarget);
  const refReturns = calculateLogReturns(alignedRef);
  const returnDates = alignedDates.slice(1);

  // Calculate rolling statistics
  const stats = calculateRollingStats(targetReturns, refReturns, returnDates, rollingWindow.value);

  // Calculate CCF for most recent window
  const recentTargetReturns = targetReturns.slice(-rollingWindow.value * 2);
  const recentRefReturns = refReturns.slice(-rollingWindow.value * 2);
  const ccf = calculateCCF(recentTargetReturns, recentRefReturns, lagRange.value);

  // Find dominant lag
  let maxAbsCorr = 0;
  let dominantLag = 0;
  let lagCorrelation = 0;
  for (let i = 0; i < ccf.correlations.length; i++) {
    if (Math.abs(ccf.correlations[i]) > maxAbsCorr) {
      maxAbsCorr = Math.abs(ccf.correlations[i]);
      dominantLag = ccf.lags[i];
      lagCorrelation = ccf.correlations[i];
    }
  }

  // Current values (latest)
  const currentBeta = stats.rollingBeta[stats.rollingBeta.length - 1];
  const currentDownsideBeta = stats.rollingDownsideBeta.filter(v => v !== null).slice(-1)[0];
  const currentCorrelation = stats.rollingCorr[stats.rollingCorr.length - 1];
  const currentSpearman = stats.rollingSpearman[stats.rollingSpearman.length - 1];
  const btcVolatility = stats.rollingVol[stats.rollingVol.length - 1];

  // Classify regime
  const btcRegime = classifyRegime(btcVolatility, stats.rollingVol);

  // Beta stability check (rolling change in beta)
  let betaStable = false;
  if (stats.rollingBeta.length >= 5) {
    const recentBetas = stats.rollingBeta.slice(-10);
    const betaChanges = [];
    for (let i = 1; i < recentBetas.length; i++) {
      betaChanges.push(Math.abs(recentBetas[i] - recentBetas[i - 1]));
    }
    const avgBetaChange = mean(betaChanges);
    betaStable = avgBetaChange < 0.5; // Threshold for stability
  }

  // Update analytics data
  analyticsData.value = {
    dates: stats.validDates,
    rollingBeta: stats.rollingBeta,
    rollingDownsideBeta: stats.rollingDownsideBeta,
    rollingCorrelation: stats.rollingCorr,
    rollingSpearman: stats.rollingSpearman,
    ccfLags: ccf.lags,
    ccfValues: ccf.correlations,
    rollingVolatility: stats.rollingVol,
    currentBeta,
    currentDownsideBeta,
    currentCorrelation,
    currentSpearman,
    dominantLag,
    lagCorrelation,
    btcRegime,
    btcVolatility,
    betaStable,
  };

  // Render analytics charts
  renderAnalyticsCharts();
};

// Render all analytics charts
const renderAnalyticsCharts = () => {
  renderBetaChart();
  renderCorrelationChart();
  renderCCFChart();
  renderRegimeChart();
};

// Render Beta chart
const renderBetaChart = () => {
  if (!betaChartContainer.value) return;

  const data = analyticsData.value;
  const traces = [
    {
      x: data.dates,
      y: data.rollingBeta,
      type: 'scatter',
      mode: 'lines',
      name: 'Beta (β)',
      line: { color: '#3b82f6', width: 2 },
    },
    {
      x: data.dates,
      y: data.rollingDownsideBeta,
      type: 'scatter',
      mode: 'lines',
      name: 'Downside β',
      line: { color: '#ef4444', width: 2, dash: 'dash' },
      connectgaps: false,
    },
    {
      x: data.dates,
      y: data.dates.map(() => 1),
      type: 'scatter',
      mode: 'lines',
      name: 'β = 1',
      line: { color: '#6b7280', width: 1, dash: 'dot' },
      showlegend: false,
    },
  ];

  const layout = {
    height: 250,
    margin: { t: 10, b: 40, l: 50, r: 20 },
    xaxis: { type: 'date' },
    yaxis: { title: 'Beta', zeroline: true },
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
    font: { color: '#9ca3af', size: 10 },
    legend: { x: 0, y: 1.15, orientation: 'h' },
    showlegend: true,
  };

  Plotly.react(betaChartContainer.value, traces, layout, { responsive: true, displayModeBar: false });
};

// Render Correlation chart
const renderCorrelationChart = () => {
  if (!correlationChartContainer.value) return;

  const data = analyticsData.value;
  const traces = [
    {
      x: data.dates,
      y: data.rollingCorrelation,
      type: 'scatter',
      mode: 'lines',
      name: 'Pearson',
      line: { color: '#10b981', width: 2 },
    },
    {
      x: data.dates,
      y: data.rollingSpearman,
      type: 'scatter',
      mode: 'lines',
      name: 'Spearman',
      line: { color: '#8b5cf6', width: 2, dash: 'dash' },
    },
    {
      x: data.dates,
      y: data.dates.map(() => correlationThreshold.value),
      type: 'scatter',
      mode: 'lines',
      name: 'Threshold',
      line: { color: '#f59e0b', width: 1, dash: 'dot' },
    },
  ];

  const layout = {
    height: 250,
    margin: { t: 10, b: 40, l: 50, r: 20 },
    xaxis: { type: 'date' },
    yaxis: { title: 'Correlation', range: [-1, 1] },
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
    font: { color: '#9ca3af', size: 10 },
    legend: { x: 0, y: 1.15, orientation: 'h' },
    showlegend: true,
  };

  Plotly.react(correlationChartContainer.value, traces, layout, { responsive: true, displayModeBar: false });
};

// Render CCF chart (bar chart)
const renderCCFChart = () => {
  if (!ccfChartContainer.value) return;

  const data = analyticsData.value;
  const colors = data.ccfLags.map(lag =>
    lag === data.dominantLag ? '#f59e0b' : (lag > 0 ? '#3b82f6' : '#6b7280')
  );

  const traces = [
    {
      x: data.ccfLags,
      y: data.ccfValues,
      type: 'bar',
      marker: { color: colors },
      name: 'CCF',
    },
  ];

  const layout = {
    height: 250,
    margin: { t: 10, b: 40, l: 50, r: 20 },
    xaxis: { title: 'Lag (bars)', dtick: 1 },
    yaxis: { title: 'Correlation', range: [-1, 1] },
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
    font: { color: '#9ca3af', size: 10 },
    showlegend: false,
    annotations: [
      {
        x: data.dominantLag,
        y: data.lagCorrelation,
        text: `k*=${data.dominantLag}`,
        showarrow: true,
        arrowhead: 2,
        ax: 0,
        ay: -30,
        font: { color: '#f59e0b', size: 11 },
      },
    ],
  };

  Plotly.react(ccfChartContainer.value, traces, layout, { responsive: true, displayModeBar: false });
};

// Render Regime/Volatility chart
const renderRegimeChart = () => {
  if (!regimeChartContainer.value) return;

  const data = analyticsData.value;

  const traces = [
    {
      x: data.dates,
      y: data.rollingVolatility,
      type: 'scatter',
      mode: 'lines',
      name: 'Volatility (σ)',
      line: { color: '#ec4899', width: 2 },
      fill: 'tozeroy',
      fillcolor: 'rgba(236, 72, 153, 0.2)',
    },
  ];

  const layout = {
    height: 250,
    margin: { t: 10, b: 40, l: 50, r: 20 },
    xaxis: { type: 'date' },
    yaxis: { title: 'Volatility' },
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
    font: { color: '#9ca3af', size: 10 },
    showlegend: true,
    legend: { x: 0, y: 1.15, orientation: 'h' },
  };

  Plotly.react(regimeChartContainer.value, traces, layout, { responsive: true, displayModeBar: false });
};

const handleClickOutside = (event) => {
  if (!event.target.closest('.dropdown')) {
    showDropdown.value = false;
    showOverlayDropdown.value = false;
  }
};

const handleResize = () => {
  if (hasData.value && chartContainer.value) {
    Plotly.Plots.resize(chartContainer.value);
  }
  // Resize analytics charts
  if (betaChartContainer.value) Plotly.Plots.resize(betaChartContainer.value);
  if (correlationChartContainer.value) Plotly.Plots.resize(correlationChartContainer.value);
  if (ccfChartContainer.value) Plotly.Plots.resize(ccfChartContainer.value);
  if (regimeChartContainer.value) Plotly.Plots.resize(regimeChartContainer.value);
};

onMounted(() => {
  // Set default dates (last 30 days)
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);

  endDate.value = end.toISOString().split('T')[0];
  startDate.value = start.toISOString().split('T')[0];

  document.addEventListener('click', handleClickOutside);
  window.addEventListener('resize', handleResize);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
  window.removeEventListener('resize', handleResize);
  if (chartContainer.value) {
    Plotly.purge(chartContainer.value);
  }
  // Clean up analytics charts
  if (betaChartContainer.value) Plotly.purge(betaChartContainer.value);
  if (correlationChartContainer.value) Plotly.purge(correlationChartContainer.value);
  if (ccfChartContainer.value) Plotly.purge(ccfChartContainer.value);
  if (regimeChartContainer.value) Plotly.purge(regimeChartContainer.value);
});

// Watch for timeframe changes to auto-refetch if data exists
watch(selectedTimeframe, async () => {
  if (hasData.value) {
    await fetchOHLCData();
  }
});

// Focus input when overlay dropdown opens
watch(showOverlayDropdown, (isOpen) => {
  if (isOpen) {
    overlaySymbolInput.value = '';
    filterOverlaySymbols();
    // Focus input on next tick
    setTimeout(() => {
      overlayInput.value?.focus();
    }, 50);
  }
});
</script>

<style scoped>
.ohlc-explorer-tab {
  width: 100%;
}

.chart-container {
  width: 100%;
  height: 500px;
  min-height: 400px;
}

.analytics-chart {
  width: 100%;
  height: 250px;
  min-height: 200px;
}

.dropdown-content {
  position: absolute;
  top: 100%;
  left: 0;
}

/* Regime badge pulse animation */
@keyframes pulse-dot {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.animate-pulse {
  animation: pulse-dot 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@media (max-width: 639px) {
  .chart-container {
    height: 360px;
    min-height: 320px;
  }
  .chart-panel-large {
    height: 360px;
    min-height: 320px;
  }
  .chart-panel,
  .analytics-chart {
    height: 280px;
    min-height: 240px;
  }
}
</style>
